import React, { useState, useEffect, useRef, useCallback } from 'react';

// ----------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------
function MorphingText({ text }) {
  const [width, setWidth] = useState("auto");
  const spanRef = useRef(null);

  useEffect(() => {
    if (spanRef.current) {
      setWidth(spanRef.current.offsetWidth + 4);
    }
  }, [text]);

  return (
    <span
      className="hero-morphing-wrapper"
      style={{ width }}
    >
      <span ref={spanRef} className="hero-morphing-measure">
        {text}
      </span>
      <span key={text} className="hero-morphing-content">
        {text}
      </span>
    </span>
  );
}

function ModelIcon({ model }) {
  if (model.includes('uAI') || model.includes('IVORI')) {
    return (
      <img
        src="/logo-yellow.png"
        alt="uAI Logo"
        style={{ width: '15px', height: '15px', objectFit: 'contain' }}
      />
    );
  }

  const icons = {
    "ChatGPT 4o": "https://res.cloudinary.com/drhx7imeb/image/upload/v1781695269/openai-icon_zozuib.svg",
    "Claude 3.5 Sonnet": "https://res.cloudinary.com/drhx7imeb/image/upload/v1781695268/Claude_AI_symbol_yqfzlc.svg",
    "Mistral Large": "https://res.cloudinary.com/drhx7imeb/image/upload/v1781695269/z-ai-icon_xi4xvo.svg"
  };

  return (
    <img 
      src={icons[model] || "https://res.cloudinary.com/drhx7imeb/image/upload/v1781695269/openai-icon_zozuib.svg"} 
      alt={model} 
      style={{ width: '14px', height: '14px', objectFit: 'contain' }} 
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
}

function ArrowUpIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="7" y1="17" x2="17" y2="7"></line>
      <polyline points="7 7 17 7 17 17"></polyline>
    </svg>
  );
}

function DynamicBarsIcon({ level }) {
  const isMediumOrHigh = level === "Medium" || level === "Max Effort";
  const isHigh = level === "Max Effort";

  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="1.5" y="8" width="2.5" height="4.5" rx="1" fill="currentColor" opacity={1} />
      <rect x="5.75" y="5" width="2.5" height="7.5" rx="1" fill="currentColor" opacity={isMediumOrHigh ? 1 : 0.3} />
      <rect x="10" y="2" width="2.5" height="10.5" rx="1" fill="currentColor" opacity={isHigh ? 1 : 0.3} />
    </svg>
  );
}

// ----------------------------------------------------------------------
// Main HeroPromptWidget Component
// ----------------------------------------------------------------------
export default function HeroPromptWidget({
  onSubmit,
  placeholder = "Ask ivori AI to analyze a case, build a flow, run code, or solve complex problems...",
  models = ["uAI (Default)", "ChatGPT 4o", "Claude 3.5 Sonnet", "Mistral Large"],
  efforts = ["Low", "Medium", "Max Effort"],
  onExploreClick,
}) {
  const [expanded, setExpanded] = useState(false);
  const [isSmoothResize, setIsSmoothResize] = useState(false);
  const [value, setValue] = useState("");
  const [selectedModel, setSelectedModel] = useState(models[0]);
  const [effortIndex, setEffortIndex] = useState(2); // Max Effort
  const [isModelSelectOpen, setIsModelSelectOpen] = useState(false);

  const [containerHeight, setContainerHeight] = useState(120);
  const [textareaHeight, setTextareaHeight] = useState(68);
  const [isScrolling, setIsScrolling] = useState(false);

  const valueRef = useRef(value);
  const textareaRef = useRef(null);
  const internalContainerRef = useRef(null);

  const hasValue = value.trim() !== "";

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const handleValueChange = useCallback((val) => {
    setIsSmoothResize(true);
    setValue(val);
  }, []);

  const expand = () => {
    setIsSmoothResize(false);
    setExpanded(true);
  };

  useEffect(() => {
    if (value.trim() !== "" && !expanded) {
      setIsSmoothResize(false);
      setExpanded(true);
    }
  }, [value, expanded]);

  useEffect(() => {
    if (expanded && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [expanded]);

  useEffect(() => {
    if (!textareaRef.current) return;
    const el = textareaRef.current;

    const currentHeight = el.style.height;
    el.style.transition = 'none';
    el.style.height = "0px";
    const scrollHeight = el.scrollHeight;
    el.style.height = currentHeight;
    void el.offsetHeight;
    el.style.transition = '';

    const newHeight = Math.max(68, Math.min(scrollHeight, 160));
    el.style.height = `${newHeight}px`;

    setTextareaHeight(newHeight);
    setIsScrolling(scrollHeight > 160);
  }, [value, expanded]);

  useEffect(() => {
    setContainerHeight(Math.max(68, textareaHeight + 16));
  }, [textareaHeight]);

  useEffect(() => {
    if (!isModelSelectOpen) return;
    const handleOutsideClick = (e) => {
      if (internalContainerRef.current && !internalContainerRef.current.contains(e.target)) {
        setIsModelSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isModelSelectOpen]);

  const handleBlur = (e) => {
    if (internalContainerRef.current && internalContainerRef.current.contains(e.relatedTarget)) return;
    if (value.trim() === "") {
      setIsSmoothResize(false);
      setExpanded(false);
      setIsModelSelectOpen(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (onSubmit) {
      onSubmit(value, { model: selectedModel, effort: efforts[effortIndex] });
    }
    setValue("");
    setExpanded(false);
    setIsModelSelectOpen(false);
  };

  const cycleEffort = (e) => {
    e.stopPropagation();
    setEffortIndex((prev) => (prev + 1) % efforts.length);
  };

  const onActionButtonClick = (e) => {
    e.preventDefault();
    handleSubmit(e);
  };

  return (
    <div
      ref={internalContainerRef}
      onBlur={handleBlur}
      className={`hero-prompt-widget-root ${expanded ? 'expanded' : 'compact'}`}
      style={{
        maxWidth: expanded ? 720 : 560,
        transition: isSmoothResize ? "max-width 0.15s ease-out" : "max-width 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
      }}
    >
      {/* Main Expandable Input Box */}
      <div
        onMouseDown={(e) => {
          if (expanded && e.target !== textareaRef.current) {
            e.preventDefault();
            textareaRef.current?.focus();
          }
        }}
        style={{
          height: expanded ? containerHeight : 54,
          transition: isSmoothResize ? "height 0.15s ease-out" : "max-width 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), height 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        }}
        className="hero-prompt-card"
      >
        {/* Compact Mode Input View */}
        {!expanded && (
          <button
            type="button"
            onClick={expand}
            className="hero-prompt-placeholder-btn"
          >
            <img src="/logo-yellow.png" alt="ivori Logo" className="hero-brand-logo-icon" />
            <span className="placeholder-text">{placeholder}</span>
          </button>
        )}

        {/* Expanded Textarea View */}
        {expanded && (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => handleValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
              if (e.key === "Escape" && value.trim() === "") {
                setIsSmoothResize(false);
                setExpanded(false);
                setIsModelSelectOpen(false);
              }
            }}
            placeholder={placeholder}
            className={`hero-prompt-textarea ${isScrolling ? 'scrollable' : ''}`}
          />
        )}

        {/* Bottom Actions Bar (Removed model dropdown and strength buttons as requested) */}

        {/* Send Arrow Up Action Button */}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={onActionButtonClick}
          aria-label="Send prompt"
          className="hero-action-btn"
        >
          <ArrowUpIcon />
        </button>
      </div>

      {/* Preset Chips & Explore Tools Row */}
      <div className="hero-prompt-chips-row">
        <button
          type="button"
          className="hero-prompt-chip"
          onClick={() => { setValue("Analyze reasoning and argument structure in complex case"); expand(); }}
        >
          + AI Reasoning & Analysis
        </button>
        <button
          type="button"
          className="hero-prompt-chip"
          onClick={() => { setValue("Build a web app prototype and flowchart diagram"); expand(); }}
        >
          + Code & Flow Builder
        </button>
        <button
          type="button"
          className="hero-prompt-chip"
          onClick={() => { setValue("Financial modeling & case simulation"); expand(); }}
        >
          + Case Modeling
        </button>
        {onExploreClick && (
          <button
            type="button"
            className="hero-prompt-chip explore"
            onClick={onExploreClick}
          >
            Explore cases made by our users ↓
          </button>
        )}
      </div>
    </div>
  );
}
